import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import Select from "react-select";
import apiClient from "../../utils/ApiClient/apiClientAxios";
import { toastService } from "../../utils/ApiClient/toastService";
import { selectStyles } from "../../utils/selectStyel";

export default function RosterTypeSelector({ importId, onSuccess }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [options, setOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  // Fetch config options when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchOptions = async () => {
      try {
        setIsLoadingOptions(true);

        const res = await apiClient.get("/listConfig", {
          params: { type: "IMPORT_ROSTER" },
        });

        const data = res.data?.data ?? res.data ?? [];

        const mapped = data.map((item) => ({
          value: item.id,
          label: item.DESCRIPTION
            ? `${item.id} — ${item.DESCRIPTION}`
            : item.id,
        }));

        setOptions(mapped);
      } catch (err) {
        console.error("Failed to load roster config:", err);
        toastService.error("Failed to load roster types.");
        setOptions([]);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [isOpen]);

  const handleRosterChange = async (option) => {
    if (!option) {
      setSelectedRoster(null);
      return;
    }

    setSelectedRoster(option);
    setIsVerifying(true);

    try {
      const response = await apiClient.get("/validateRoster", {
        params: {
          import_id: importId,
          roster_id: option.value,
        },
      });

      toastService.success(`${option.label} has been verified successfully`);

      if (onSuccess) onSuccess(response.data);

      // Close modal after short delay
      setTimeout(() => {
        onClose();
        setSelectedRoster(null);
      }, 500);
    } catch (error) {
      console.error("Error verifying roster:", error);
      toastService.error("Failed to verify roster type.");
      setSelectedRoster(null);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        bg="gray.800"
        color="white"
        borderRadius="sm"
        fontWeight="500"
        px={5}
        _hover={{ bg: "gray.700" }}
        _active={{ bg: "gray.900" }}
        onClick={onOpen}
      >
        Change Roster Type
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        size="md"
        closeOnOverlayClick={!isVerifying}
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader fontSize="lg" fontWeight="600">
            Select Roster Type
          </ModalHeader>
          <ModalCloseButton isDisabled={isVerifying} />

          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Choose the roster type for import ID:
              </Text>

              <Select
                value={selectedRoster}
                onChange={handleRosterChange}
                options={options}
                placeholder={
                  isLoadingOptions
                    ? "Loading roster types..."
                    : "Select a roster type..."
                }
                isSearchable
                isClearable
                isDisabled={isVerifying || isLoadingOptions}
                isLoading={isVerifying || isLoadingOptions}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />

              {isVerifying && (
                <Text fontSize="sm" color="blue.500" fontWeight="500">
                  Verifying roster type...
                </Text>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
